import gql from "graphql-tag";
import GraphQLClient, { RequestParam } from "../api";
import {
  Wallet,
  ListFishPackageResponse,
  ListFishPackageInput,
  FishProduct,
  ListTransactionHistoryInput,
  ListTransactionHistoryOutput,
} from "../models/nolaCoin";
import { logError, createApiLogContext } from "../utils/logger";

/**
 * NolaCoin API クライアントインターフェース
 */
interface INolaCoinClient {
  getWallet: () => Promise<Wallet>;
  getFishProducts: () => Promise<FishProduct[]>;
  listTransactionHistory: (input?: ListTransactionHistoryInput) => Promise<ListTransactionHistoryOutput>;
}

/**
 * NolaCoin API クライアント
 * コイン残高情報の取得、Fish商品情報の取得、Fish履歴の取得を行う
 */
export class NolaCoinClient implements INolaCoinClient {
  /**
   * ウォレット情報を取得
   * Fish残高として使用される現在のコイン残高を取得
   *
   * @returns Promise<Wallet> ウォレット情報（残高含む）
   */
  async getWallet(): Promise<Wallet> {
    const params: RequestParam = {
      query: getWalletQuery,
      variables: {
        input: {
          platform: "WEB",
        },
      },
    };

    try {
      const { getWallet } = await GraphQLClient.request(params);
      return getWallet;
    } catch (error: unknown) {
      logError(createApiLogContext("NolaCoin", "ウォレット情報取得"), "ウォレット情報の取得に失敗しました", error);
      throw error;
    }
  }

  /**
   * バックエンドからFish商品情報を取得
   */
  async getBackendFishProducts(): Promise<FishProduct[]> {
    const input: ListFishPackageInput = { platform: "WEB" };
    const params: RequestParam = {
      query: listFishPackageQuery,
      variables: {
        input,
      },
    };

    try {
      const response: ListFishPackageResponse = await GraphQLClient.request(params);
      return response.listFishPackage;
    } catch (error: unknown) {
      logError(createApiLogContext("NolaCoin", "バックエンドFish商品取得"), "Fish商品情報の取得に失敗しました", error);

      // エラーを上位に伝播させてエラー表示と再取得機能を有効にする
      throw new Error("Fish商品情報の取得に失敗しました");
    }
  }

  /**
   * バックエンドからFish商品情報を取得してFishProduct形式に変換
   */
  async getFishProducts(): Promise<FishProduct[]> {
    try {
      const backendProducts = await this.getBackendFishProducts();
      // 価格順でソート
      return backendProducts.sort((a, b) => a.price - b.price);
    } catch (error: unknown) {
      logError(createApiLogContext("NolaCoin", "Fish商品統合"), "Fish商品リストの生成に失敗しました", error);
      throw error;
    }
  }

  /**
   * Fish履歴を取得
   * ページネーション対応でFish取得・利用履歴を取得
   *
   * @param input ListTransactionHistoryInput 取得条件
   * @returns Promise<ListTransactionHistoryOutput> Fish履歴リスト
   */
  async listTransactionHistory(input: ListTransactionHistoryInput = {}): Promise<ListTransactionHistoryOutput> {
    const params: RequestParam = {
      query: listTransactionHistoryQuery,
      variables: {
        input: {
          limit: input.limit || 100,
          lastKey: input.lastKey,
        },
      },
    };

    try {
      const { listTransactionHistory } = await GraphQLClient.request(params);
      return listTransactionHistory;
    } catch (error: unknown) {
      logError(createApiLogContext("NolaCoin", "Fish履歴取得"), "Fish履歴の取得に失敗しました", error);
      throw error;
    }
  }
}

/**
 * GraphQL クエリ定義
 */

const getWalletQuery = gql`
  query GetWallet($input: GetWalletInput!) {
    getWallet(input: $input) {
      userId
      amount
      totalAmount
      walletBreakdown {
        coinType
        amount
      }
    }
  }
`;

const listFishPackageQuery = gql`
  query ListFishPackage($input: ListFishPackageInput!) {
    listFishPackage(input: $input) {
      id
      amount
      bonus
      currency
      price
    }
  }
`;

const listTransactionHistoryQuery = gql`
  query ListTransactionHistory($input: ListTransactionHistoryInput!) {
    listTransactionHistory(input: $input) {
      items {
        userId
        transactionId
        amount
        transactionType
        entryBreakdown {
          entryId
          coinType
          amount
          expiredAt
        }
        referenceId
        createdAt
        updatedAt
      }
      lastKey
    }
  }
`;
